import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge, Button, Card, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Field, Input, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger,
  Checkbox, MessageBar, MessageBarBody, Select, Spinner, Table, TableBody, TableCell, TableCellLayout, TableHeader, TableHeaderCell, TableRow, Text, makeStyles, shorthands, tokens,
} from '@fluentui/react-components';
import { AddRegular, MoreHorizontalRegular } from '@fluentui/react-icons';
import { GRANT_ROLES, GRANT_ROLE_LABELS, MIN_PASSWORD_LENGTH, ssoState, ROLES, ROLE_LABELS, USER_KINDS, USER_KIND_LABELS, defaultGrantRole, type GrantRole, type InstanceView, type MailSettingsView, type Role, type UserGrant, type UserKind, type UserView } from '@vxh/shared';
import { api } from '../api';
import { PageHeader } from '../components/PageHeader';
import { SSO_STATE_HELP } from '../components/StatusPill';
import { ago } from '../format';

const useStyles = makeStyles({ grid: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', ...shorthands.gap('14px') }, tableWrap: { overflowX: 'auto' } });

/** A password people can be told once: 20 letters and digits, no look-alikes. */
function suggestPassword(): string {
  const chars = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = crypto.getRandomValues(new Uint8Array(20));
  return Array.from(bytes, (b) => chars[b % chars.length]).join('');
}

export function UsersPage() {
  const s = useStyles();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['users'], queryFn: () => api<UserView[]>('/users') });
  const [adding, setAdding] = useState(false);
  const [resetting, setResetting] = useState<UserView | null>(null);
  const [choosing, setChoosing] = useState<UserView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Invitations are emailed by the hub's mail service, so the menu item needs it to be on and ready.
  const mail = useQuery({ queryKey: ['mail-settings'], queryFn: () => api<MailSettingsView>('/mail/settings') });
  const mailReady = mail.data?.ready === true;
  const refresh = () => qc.invalidateQueries({ queryKey: ['users'] });
  const patch = useMutation({
    mutationFn: (v: { id: string; body: Record<string, unknown> }) => api(`/users/${v.id}`, { method: 'PATCH', body: v.body }),
    onSuccess: () => (setError(null), setNotice(null), void refresh()),
    onError: (e) => (setNotice(null), setError((e as Error).message)),
  });
  const invite = useMutation({
    mutationFn: (u: UserView) => api(`/users/${u.id}`, { method: 'PATCH', body: { resendInvite: true } }),
    onSuccess: (_r, u) => (setError(null), setNotice(`Invitation sent to ${u.email}. Any earlier invitation no longer works. The link is valid for 7 days.`), void refresh()),
    onError: (e) => (setNotice(null), setError((e as Error).message)),
  });

  return (
    <>
      <PageHeader title="People" subtitle="Who can sign in to the hub, and which instances each person can open. A person's role on an instance is what they get when they sign in to it with their hub account (if the instance allows it)." actions={<Button appearance="primary" icon={<AddRegular />} onClick={() => setAdding(true)}>Add a person</Button>} />
      {error && (
        <MessageBar intent="error" style={{ marginBottom: 12 }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
      {notice && (
        <MessageBar intent="success" style={{ marginBottom: 12 }}>
          <MessageBarBody>{notice}</MessageBarBody>
        </MessageBar>
      )}
      <Card>
        {q.isLoading ? <Spinner label="Loading" /> : q.isError ? (
          <MessageBar intent="error"><MessageBarBody>{(q.error as Error).message}</MessageBarBody></MessageBar>
        ) : (
          <div className={s.tableWrap}>
            <Table aria-label="People">
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Name</TableHeaderCell>
                  <TableHeaderCell>Account</TableHeaderCell>
                  <TableHeaderCell>Instances</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Authenticator</TableHeaderCell>
                  <TableHeaderCell>Last sign-in</TableHeaderCell>
                  <TableHeaderCell aria-label="Actions" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(q.data ?? []).map((u) => (
                  <TableRow key={u.id}>
                    <TableCell><TableCellLayout description={u.email}><Text weight="semibold">{u.displayName}</Text></TableCellLayout></TableCell>
                    <TableCell>{u.kind === 'customer' ? USER_KIND_LABELS.customer : `${USER_KIND_LABELS.staff} · ${ROLE_LABELS[u.role]}`}</TableCell>
                    <TableCell>{u.role === 'ADMIN' ? (u.instanceCount ? `All open · ${u.instanceCount} sign-in roles` : 'All open') : u.instanceCount === 0 ? 'None yet' : `${u.instanceCount} assigned`}</TableCell>
                    <TableCell>
                      <Badge appearance="tint" color={u.status === 'active' ? 'success' : 'subtle'} shape="rounded">{u.status === 'active' ? 'Active' : 'Switched off'}</Badge>
                      {u.invitePending && <Badge appearance="outline" color="warning" shape="rounded" style={{ marginLeft: 6 }}>Invited</Badge>}
                    </TableCell>
                    <TableCell>{u.mfaEnrolled ? 'Set up' : 'Not yet'}</TableCell>
                    <TableCell>{ago(u.lastLoginAt)}</TableCell>
                    <TableCell>
                      <Menu>
                        <MenuTrigger disableButtonEnhancement>
                          <Button appearance="subtle" size="small" icon={<MoreHorizontalRegular />} aria-label={`More actions for ${u.displayName}`} />
                        </MenuTrigger>
                        <MenuPopover>
                          <MenuList>
                            <MenuItem disabled={u.kind === 'customer'} onClick={() => patch.mutate({ id: u.id, body: { role: u.role === 'ADMIN' ? 'STAFF' : 'ADMIN' } })}>{u.role === 'ADMIN' ? 'Make staff' : 'Make administrator'}</MenuItem>
                            <MenuItem disabled={u.role === 'ADMIN'} onClick={() => patch.mutate({ id: u.id, body: { kind: u.kind === 'customer' ? 'staff' : 'customer' } })}>{u.kind === 'customer' ? 'Change to Voxshift staff' : 'Change to customer account'}</MenuItem>
                            <MenuItem onClick={() => setChoosing(u)}>Choose instances and roles</MenuItem>
                            {/* For anyone who has not finished signing in for the first time (no authenticator set up yet). */}
                            {!u.mfaEnrolled && u.status === 'active' && (
                              <MenuItem disabled={!mailReady || invite.isPending} onClick={() => invite.mutate(u)}>
                                {!mailReady ? 'Email an invitation (set up the mail service first)' : u.invitePending ? 'Email the invitation again' : 'Email an invitation'}
                              </MenuItem>
                            )}
                            <MenuItem onClick={() => setResetting(u)}>Set a new password</MenuItem>
                            <MenuItem disabled={!u.mfaEnrolled} onClick={() => patch.mutate({ id: u.id, body: { resetMfa: true } })}>Reset authenticator</MenuItem>
                            <MenuItem onClick={() => patch.mutate({ id: u.id, body: { status: u.status === 'active' ? 'disabled' : 'active' } })}>{u.status === 'active' ? 'Switch off' : 'Switch on'}</MenuItem>
                          </MenuList>
                        </MenuPopover>
                      </Menu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
      {choosing && <AccessDialog user={choosing} onClose={() => setChoosing(null)} onSaved={() => (setChoosing(null), void refresh())} />}
      {adding && <AddDialog onClose={() => setAdding(false)} onDone={() => (setAdding(false), void refresh())} />}
      {resetting && (
        <PasswordDialog
          user={resetting}
          onClose={() => setResetting(null)}
          onSave={(password) => patch.mutate({ id: resetting.id, body: { password } }, { onSuccess: () => setResetting(null) })}
        />
      )}
    </>
  );
}

function PasswordField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Field label="Temporary password" required hint={`At least ${MIN_PASSWORD_LENGTH} characters. Pass it on securely; they must replace it at their first sign-in.`}>
      <Input value={value} onChange={(_, d) => onChange(d.value)} contentAfter={<Button appearance="transparent" size="small" onClick={() => onChange(suggestPassword())}>Suggest</Button>} />
    </Field>
  );
}

function AddDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const s = useStyles();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [kind, setKind] = useState<UserKind>('staff');
  const [role, setRole] = useState<Role>('STAFF');
  const [password, setPassword] = useState(suggestPassword());
  const [error, setError] = useState<string | null>(null);
  const mail = useQuery({ queryKey: ['mail-settings'], queryFn: () => api<MailSettingsView>('/mail/settings') });
  const canInvite = mail.data?.ready === true;
  // When the mail service is set up the default is to email an invitation (the person chooses their own password); otherwise an administrator passes on a starter password.
  const [method, setMethod] = useState<'invite' | 'password' | null>(null);
  const how = method ?? (canInvite ? 'invite' : 'password');
  const save = useMutation({
    mutationFn: () => api('/users', { method: 'POST', body: { email, displayName, kind, role: kind === 'customer' ? 'STAFF' : role, ...(how === 'invite' ? { invite: true } : { password }) } }),
    onSuccess: onDone,
    onError: (e) => setError((e as Error).message),
  });
  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Add a person</DialogTitle>
          <DialogContent className={s.grid}>
            <Field label="Email" required><Input type="email" value={email} onChange={(_, d) => setEmail(d.value)} autoFocus /></Field>
            <Field label="Name" required><Input value={displayName} onChange={(_, d) => setDisplayName(d.value)} /></Field>
            <Field label="Account type" hint={kind === 'customer' ? 'A customer sees only the instances you choose for them, and nothing else in the hub.' : 'Voxshift staff and MSP engineers.'}>
              <Select value={kind} onChange={(_, d) => { setKind(d.value as UserKind); if (d.value === 'customer') setRole('STAFF'); }}>
                {USER_KINDS.map((k) => <option key={k} value={k}>{USER_KIND_LABELS[k]}</option>)}
              </Select>
            </Field>
            {kind === 'staff' && (
              <Field label="Role" hint="Administrators manage instances and people. Staff can see and open the instances you choose for them.">
                <Select value={role} onChange={(_, d) => setRole(d.value as Role)}>
                  {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                </Select>
              </Field>
            )}
            <Field label="How do they get started?" hint={canInvite ? undefined : 'Invitations by email need the mail service (Settings).'}>
              <Select value={how} onChange={(_, d) => setMethod(d.value as 'invite' | 'password')}>
                <option value="invite" disabled={!canInvite}>Email them an invitation link</option>
                <option value="password">I will pass on a starter password</option>
              </Select>
            </Field>
            {how === 'password' && <PasswordField value={password} onChange={setPassword} />}
            {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>Cancel</Button>
            <Button appearance="primary" disabled={!email || !displayName.trim() || (how === 'password' && password.length < MIN_PASSWORD_LENGTH) || save.isPending} onClick={() => save.mutate()}>{how === 'invite' ? 'Send invitation' : 'Add'}</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function PasswordDialog({ user, onClose, onSave }: { user: UserView; onClose: () => void; onSave: (password: string) => void }) {
  const s = useStyles();
  const [password, setPassword] = useState(suggestPassword());
  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>New password for {user.displayName}</DialogTitle>
          <DialogContent className={s.grid}>
            <PasswordField value={password} onChange={setPassword} />
            <Text>Setting it signs them out everywhere.</Text>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>Cancel</Button>
            <Button appearance="primary" disabled={password.length < MIN_PASSWORD_LENGTH} onClick={() => onSave(password)}>Set password</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

/** Which instances a person may open, and the role they get in each when they sign in to it with their hub account. */
function AccessDialog({ user, onClose, onSaved }: { user: UserView; onClose: () => void; onSaved: () => void }) {
  const s = useStyles();
  const instances = useQuery({ queryKey: ['instances'], queryFn: () => api<InstanceView[]>('/instances') });
  const current = useQuery({ queryKey: ['user-access', user.id], queryFn: () => api<{ grants: UserGrant[] }>(`/users/${user.id}/instances`) });
  const [picked, setPicked] = useState<Map<string, GrantRole> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const chosen = picked ?? new Map((current.data?.grants ?? []).map((g) => [g.instanceId, g.role] as const));
  const set = (id: string, role: GrantRole | null) => {
    const next = new Map(chosen);
    if (role) next.set(id, role);
    else next.delete(id);
    setPicked(next);
  };
  const save = useMutation({
    mutationFn: () => api(`/users/${user.id}/instances`, { method: 'PUT', body: { grants: [...chosen].map(([instanceId, role]) => ({ instanceId, role })) } }),
    onSuccess: onSaved,
    onError: (e) => setError((e as Error).message),
  });
  const loading = instances.isLoading || current.isLoading;
  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Instances for {user.displayName}</DialogTitle>
          <DialogContent className={s.grid}>
            {loading ? (
              <Spinner label="Loading" />
            ) : (instances.data ?? []).length === 0 ? (
              <Text>No instances are registered yet.</Text>
            ) : (
              <>
                <Text>
                  {user.role === 'ADMIN'
                    ? 'Administrators can open every instance. Tick an instance to also let them sign in to it with single sign-on, with the role you choose.'
                    : 'They see, and can open, only the ticked instances, with the role you choose when they sign in with single sign-on. Taking one away cuts them off from it straight away.'}{' '}
                  Each instance can still lower the role.
                </Text>
                {(instances.data ?? []).map((i) => {
                  const role = chosen.get(i.id);
                  const sso = ssoState(i);
                  return (
                    <div key={i.id} style={{ display: 'grid', gap: 2 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'space-between' }}>
                        <Checkbox checked={!!role} onChange={(_, d) => set(i.id, d.checked ? defaultGrantRole(user.kind) : null)} label={i.customerName ? `${i.name} (${i.customerName})` : i.name} />
                        <Select aria-label={`Role on ${i.name}`} disabled={!role} value={role ?? defaultGrantRole(user.kind)} onChange={(_, d) => set(i.id, d.value as GrantRole)} style={{ minWidth: 170 }}>
                          {GRANT_ROLES.map((r) => <option key={r} value={r}>{GRANT_ROLE_LABELS[r]}</option>)}
                        </Select>
                      </div>
                      {/* The role still saves, so it is ready when single sign-on is switched on; the person just opens the instance's own login until then. */}
                      {sso !== 'on' && <Text size={200} style={{ marginLeft: 32, color: tokens.colorNeutralForeground3 }}>The role is not used yet: {SSO_STATE_HELP[sso]}</Text>}
                    </div>
                  );
                })}
              </>
            )}
            {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>Cancel</Button>
            <Button appearance="primary" disabled={loading || save.isPending} onClick={() => save.mutate()}>Save</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
